import {
  AfterContentChecked,
  AfterContentInit,
  afterRenderEffect,
  AfterRenderRef,
  AfterViewChecked,
  AfterViewInit,
  ChangeDetectorRef,
  Component,
  effect,
  ElementRef,
  HostBinding,
  inject,
  OnDestroy,
  OnInit,
  signal,
  ViewChild,
  afterEveryRender,
  model, WritableSignal, computed,
} from '@angular/core';
import { TranslateService, Translation } from '@ngx-translate/core';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { PatientService } from '@core/services/patient.service';
import moment from 'moment';
import { DashedDatePipe } from '@core/pipes/dashed-date.pipe';
import { DatePipe } from '@angular/common';
import {
  ButtonComponent,
  ButtonOptions,
  FormComponent,
  ModalComponent,
  ModalOptions,
  SelectOptions,
} from '@shared/components';
import { assets, PROJECTS } from '@core/constants';
import { NavigationEnd, Router, RouterOutlet } from '@angular/router';
import {filter, fromEvent, interval, map} from 'rxjs';
import { NgbNav } from '@ng-bootstrap/ng-bootstrap';
import { APIService, DataService } from '@core/services';
import { ButtonTypes, ModalTypes } from '@core/enums';
import { HttpHeaders } from '@angular/common/http';
import { Response } from '@core/classes/other/response';
import { Version } from '@core/classes/tables/version';
import { CurrentVersion } from '@core/classes/other/current-version';
import { PatientSession } from '@core/classes/other/patient-session';
import { environment } from '@env/environment';
import {PatientVersion} from '@core/classes/other/patient-version';

@UntilDestroy()
@Component({
  selector: 'ncp-patient',
  imports: [DatePipe, RouterOutlet, ButtonComponent, ModalComponent],
  templateUrl: './patient.component.html',
  styleUrl: './patient.component.scss',
})
export class PatientComponent implements OnInit, AfterContentChecked, AfterViewInit, OnDestroy {
  @ViewChild('demographics') public demographics!: ElementRef<HTMLDivElement>;
  @ViewChild('editBtn') public editBtn!: ButtonComponent;
  @ViewChild('discardBtn') public discardBtn!: ButtonComponent;
  @ViewChild('saveBtn') public saveBtn!: ButtonComponent;
  @ViewChild('mod') public mod!: ModalComponent;
  @ViewChild('editOrcdBtn') public editOrcdBtn!: ButtonComponent;
  @ViewChild('backOrcdBtn') public backOrcdBtn!: ButtonComponent;
  private readonly changeDetectorRef: ChangeDetectorRef = inject(ChangeDetectorRef);
  private readonly translate = inject(TranslateService);
  protected readonly router = inject(Router);
  protected readonly dataService = inject(DataService);
  protected readonly apiService = inject(APIService);
  protected readonly patientService = inject(PatientService);
  protected translations: Translation = this.translate.instant('patient');
  protected modalTranslations: Translation = this.translate.instant('modal');
  protected readonly nationalSrc = assets.img.home.national.src;
  protected readonly europeanSrc = assets.img.home.european.src;
  protected readonly profileSrc = assets.img.home.profile.src;
  protected editButtonOptions = signal<ButtonOptions>({});
  protected saveButtonOptions = signal<ButtonOptions>({});
  protected discardButtonOptions = signal<ButtonOptions>({});
  protected consentModalOptions = signal<ModalOptions>({});
  protected editOrcdButtonOptions = signal<ButtonOptions>({});
  protected backOrcdButtonOptions = signal<ButtonOptions>({});
  protected versions = model<Version[]>([]);
  protected consentMessage = '';
  protected patientPhone =  computed(() => '');
  protected pPhone = '';

  @ViewChild('demographics') set dem(el: ElementRef<HTMLDivElement>) {}

  patientEffect = effect(() => {});

  constructor() {
    afterEveryRender(() => {
      this.onResize('');
    });
  }

  ngOnInit(): void {
    if (environment.project === PROJECTS.PROVIDERS) {
      this.editButtonOptions.set(
        new ButtonOptions({
          id: 'edit_ps',
          class: 'btn btn-primary editButton',
          icon: 'bi bi-pencil',
          tooltip: 'edit_ps',
          type: ButtonTypes.button,
          onClick: this.goToEdit,
        })
      );

      this.saveButtonOptions.set(
        new ButtonOptions({
          id: 'save_ps',
          class: 'btn btn-success saveButton',
          icon: 'bi bi-floppy',
          tooltip: 'save_ps',
          type: ButtonTypes.button,
          onClick: this.onSavePs,
        })
      );

      this.discardButtonOptions.set(
        new ButtonOptions({
          id: 'discard_ps',
          class: 'btn btn-danger me-2 discardButton',
          icon: 'bi bi-trash',
          tooltip: 'discard_ps',
          type: ButtonTypes.button,
          onClick: this.onDiscardPs,
        })
      );

      this.editOrcdButtonOptions.set(
        new ButtonOptions({
          id: 'add_orcd',
          class: 'btn btn-primary editButton',
          icon: 'bi bi-pencil',
          tooltip: 'add_orcd',
          type: ButtonTypes.button,
          onClick: this.goToOrcdCreate,
        })
      );

      this.backOrcdButtonOptions.set(
        new ButtonOptions({
          id: 'save_orcd',
          class: 'btn btn-success saveButton',
          icon: 'bi bi-floppy',
          tooltip: 'save_orcd',
          type: ButtonTypes.button,
          onClick: this.goToOrcdView,
        })
      );

      this.consentModalOptions.set(
        new ModalOptions({
          id: 'consentModal',
          type: ModalTypes.confirm,
          title: 'edit_ps',
          message: 'consent_edit_ps',
          onYes: this.onConsent,
          options: {
            animation: true,
            size: 'lg',
            backdrop: 'static',
            keyboard: false,
          },
        })
      );

      if (!sessionStorage.getItem('patient')) {
        this.router.navigate(['cy']);
      }
    }

    this.makeRequests();

    this.patientService.init();

    this.translate.onLangChange.pipe(untilDestroyed(this)).subscribe((event) => {
      this.translations = this.translate.instant('patient');
      this.modalTranslations = this.translate.instant('modal');
    });

    this.router.events.subscribe((event) => {
      if (event instanceof NavigationEnd) {
        this.dataService.setDataVar('headerHeight', this.demographics.nativeElement.offsetHeight);
        this.dataService.setDataVar('headerTop', this.demographics.nativeElement.offsetTop);
      }
    });

  }

  ngAfterContentChecked(): void {
    this.patientService.patientObservable.subscribe({
      next: (data: PatientSession | null | undefined) => {
        let phone = (data as PatientSession)?.data?.phone || '';
        this.patientPhone = computed(() => phone);
      }
    })
  }

  ngAfterViewInit() {
    this.dataService.setDataVar('headerHeight', this.demographics.nativeElement.offsetHeight);
    this.dataService.setDataVar('headerTop', this.demographics.nativeElement.offsetTop);
  }

  ngOnDestroy(): void {
    this.patientService.patient = null;
  }

  protected makeRequests() {
    this.apiService
      .callMany([
        {
          path: '/get-ps-versioning',
          headers: new HttpHeaders().set('pToken', this.patientService.pToken || ''),
          name: 'versions',
        },
        ...(environment.project === PROJECTS.PROVIDERS
          ? [
              {
                path: '/current-version',
                headers: new HttpHeaders().set('pToken', this.patientService.pToken || ''),
                name: 'currentVersion',
              },
            ]
          : []),
      ])
      ?.subscribe((res) => {
        let patient = JSON.parse(sessionStorage.getItem('patient')!) as PatientSession;

        if (res && res['versions']) {
          const versioning = res['versions'] as Response<Version>;
          if (versioning && versioning.data) {
            this.versions.set(versioning.data);
            this.dataService.setDataVar('versions', versioning.data);
          }
        }

        if (res && res['currentVersion']) {
          const currentVersion = res['currentVersion'] as CurrentVersion;
          if (currentVersion && currentVersion.version_id && currentVersion.version_id > -1) {
            if (patient && patient.version && patient.version.new) {
              patient.version.new = currentVersion.version_id;
              patient.version.new_dt = currentVersion.v_dt;
              sessionStorage.setItem('patient', JSON.stringify(patient));
            }
          }
        }
      });
  }

  onResize(event: any) {
    const rect = this.demographics.nativeElement.getBoundingClientRect();
    const height = rect.height;

    if (
      this.dataService.getValueByKey('headerHeight') != rect.height ||
      this.dataService.getValueByKey('headerTop') != rect.top
    ) {
      this.dataService.setDataVar('headerHeight', rect.height);
      this.dataService.setDataVar('headerTop', rect.top);
    }
  }

  goToEdit = () => {
    const version = this.patientService.version?.new;
    if (version && version > -1) {
      this.router.navigate(['cy', 'patient', 'ps', 'edit']).then();
    } else if (version && version == -1) {
      this.apiService
        .call({
          path: '/get-old-unsaved-version',
          headers: new HttpHeaders().set('pToken', this.patientService.pToken || ''),
        })
        ?.subscribe((res) => {
          if (res.data.status === 1) {
            if (res.data.data[0].version_id === -1) {
              // TODO: Handle loading after get-old-version
            } else {
              let patient = JSON.parse(sessionStorage.getItem('patient')!) as PatientSession;
              if (patient && patient.version && patient.version.new) {
                patient.version.new = res.data.data[0].version_id;
                sessionStorage.setItem('patient', JSON.stringify(patient));
                this.router.navigate(['cy', 'patient', 'ps', 'edit']).then();
              }
            }
          }
        });
      this.consentMessage = this.modalTranslations.messages.consent_ps
        .replace('{{name}}', this.patientService.data?.firstname)
        .replace('{{surname}}', this.patientService.data?.lastname);
      this.editBtn.blur();
      this.mod.open();
    }
  };

  onConsent = () => {
    this.apiService
      .call({
        path: '/insert-newversion',
        headers: new HttpHeaders().set('pToken', this.patientService.pToken || ''),
      })
      ?.subscribe((res) => {
        if (res.status === 1) {
          let patient = JSON.parse(sessionStorage.getItem('patient')!) as PatientSession;
          if (patient && patient.version && patient.version.new && patient.version.id) {
            patient.version.new = res.new_version;
            patient.version.id = -1;
            sessionStorage.setItem('patient', JSON.stringify(patient));
            this.router.navigate(['cy', 'patient', 'ps', 'edit']).then(() => {
              console.log(this.mod);
              this.mod.close();
            });
          }
        }
      });
    //this.router.navigate(['cy', 'patient', 'ps', 'edit']);
    //this.mod.close();
  };

  onSavePs = () => {
    this.apiService
      .call({
        path: '/save-psversion',
        headers: new HttpHeaders().set('pToken', this.patientService.pToken || ''),
        body: {
          version_id: this.patientService.version?.new,
        },
      })
      ?.subscribe((res) => {
        if (res.status === 1) {
          this.apiService
            .callMany([
              {
                path: '/latested-version',
                headers: new HttpHeaders().set('pToken', this.patientService.pToken || ''),
                name: 'lvres'
              },
              {
                path: '/get-ps-versioning',
                headers: new HttpHeaders().set('pToken', this.patientService.pToken || ''),
                name: 'versions',
              }
            ])
            ?.subscribe((res) => {
              if (res && res['versions']) {
                const versioning = res['versions'] as Response<Version>;
                if (versioning && versioning.data) {
                  this.versions.set(versioning.data);
                  this.dataService.setDataVar('versions', versioning.data);
                }
              }

              if (res && res['lvres']) {
                const lvrs = res['lvres'] as PatientVersion;
                let patient = JSON.parse(sessionStorage.getItem('patient')!) as PatientSession;
                if (patient && patient.version) {
                  patient.version.new = -1;
                  patient.version.id = -1;
                  patient.version.details = null;
                  patient.version.latest_dt = lvrs.v_dt;
                  patient.version.latest_author = lvrs.author;
                  sessionStorage.setItem('patient', JSON.stringify(patient));
                  this.router.navigate(['cy', 'patient', 'ps']);
                }
              }
            });
        }
      });
  };

  onDiscardPs = () => {
    this.apiService
      .call({
        path: '/discard-psversion',
        headers: new HttpHeaders().set('pToken', this.patientService.pToken || ''),
        body: {
          version_id: this.patientService.version?.new,
        },
      })
      ?.subscribe((res) => {
        if (res.status === 1) {
          let patient = JSON.parse(sessionStorage.getItem('patient')!) as PatientSession;
          if (patient && patient.version) {
            patient.version.new = -1;
            patient.version.id = -1;
            patient.version.details = null;
            sessionStorage.setItem('patient', JSON.stringify(patient));
            this.router.navigate(['cy', 'patient', 'ps']);
          }
        }
      });
  };

  goToOrcdCreate = () => {
    this.editOrcdBtn?.blur();
    this.router.navigate(['cy', 'patient', 'orcd', 'create']).then();
  };

  goToOrcdView = () => {
    this.backOrcdBtn?.blur();
    this.router.navigate(['cy', 'patient', 'orcd']).then();
  };
  protected readonly environment = environment;
  protected readonly PROJECTS = PROJECTS;
}
